import { createContext, useContext, useEffect, useState } from 'react'
import { useHost, useInstance, usePublish, useUpdateEffect, useView } from '@10000/react'

const context = createContext({})

export const useAnalyticsData = () => useContext(context)

export const AnalyticsData = ({ displayMode, children }) => {
  const { entity } = useInstance()
  const host = useHost()
  const publish = usePublish('squiz')
  const { presenter, analytics = {}, results: squizResults } = useView('squiz')

  const { results = [] } = useView('results')
  const { teams } = useView('tenant')

  const [teamId, setTeamId] = useState(results?.[results.length - 1]?.teamId || teams?.[0]?.id)
  const [compareConsensus, setCompareConsensus] = useState(false)
  const [industry, setIndustry] = useState(analytics.industry || '')
  const [role, setRole] = useState(analytics.role || '')
  const [level, setLevel] = useState(analytics.level || '')

  const [lookups, setLookups] = useState({ industries: [], roles: [], levels: [] })
  useEffect(() => {
    if(!displayMode) {
      host.getLookupValues().then(setLookups)
    }
  }, [displayMode, host])

  const filters = { teamId, compareConsensus, industry, role, level }
  const setFilters = ({ teamId, compareConsensus, industry, role, level }) => {
    teamId && setTeamId(teamId)
    compareConsensus !== undefined && setCompareConsensus(compareConsensus)

    setIndustry(industry)
    setRole(role)
    setLevel(level)
  }
  const removeFilter = filter => setFilters({ ...filters, [filter]: '' })

  const [compare, setCompare] = useState()
  useEffect(() => {
    if(presenter || !squizResults) {
      host.getPracticeAverages({ industry, role, level }).then(setCompare)
    }
  }, [host, industry, role, level, presenter, squizResults])

  const squizzes = results.length > 0 ? results.filter(x => x.teamId === teamId) : squizResults
  const [squiz, setSquiz] = useState()
  const [practice, setPractice] = useState(analytics.practice)
  const [subpractice, setSubpractice] = useState(analytics.subpractice)
  const [answer, setAnswer] = useState(analytics.answer)
  const [mode, setMode] = useState(analytics.mode || 'yourData')

  const [detailOpen, setDetailOpen] = useState(analytics.detailOpen)
  const [breakdownOpen, setBreakdownOpen] = useState(analytics.breakdownOpen)
  const [guidanceOpen, setGuidanceOpen] = useState(analytics.guidanceOpen)
  const [nextStepsOpen, setNextStepsOpen] = useState(analytics.nextStepsOpen)
  const [transition, setTransition] = useState()

  useUpdateEffect(() => {
    if(entity === 'squiz' && presenter) {
      publish('analyticsSelected', {
        teamId, compareConsensus, squiz, practice, subpractice, answer, mode,
        detailOpen, breakdownOpen, guidanceOpen, nextStepsOpen, industry, role, level,
      })
    }
  }, [
    teamId, compareConsensus, squiz, practice, subpractice, answer, mode,
    detailOpen, breakdownOpen, guidanceOpen, nextStepsOpen, industry, role, level,
    entity, publish, presenter
  ])

  useEffect(() => {
    if(displayMode && analytics) {
      setTeamId(analytics.teamId)
      setCompareConsensus(analytics.compareConsensus)
      setSquiz(analytics.squiz)
      setPractice(analytics.practice)
      setSubpractice(analytics.subpractice)
      setAnswer(analytics.answer)
      setMode(analytics.mode)
      setDetailOpen(analytics.detailOpen)
      setBreakdownOpen(analytics.breakdownOpen)
      setGuidanceOpen(analytics.guidanceOpen)
      setNextStepsOpen(analytics.nextStepsOpen)
      setIndustry(analytics.industry)
      setRole(analytics.role)
      setLevel(analytics.level)
    }
  }, [displayMode, analytics])

  const resetSelection = target => (...args) => {
    setPractice(undefined)
    setDetailOpen(false)
    setIndustry(undefined)
    setRole(undefined)
    setLevel(undefined)
    return target(...args)
  }

  const updateSquiz = squiz => setSquiz(squiz === squizzes.length - 1 ? undefined : squiz)

  return (
    <context.Provider value={{
      squizzes,
      squiz: squiz ?? squizzes.length - 1,
      setSquiz: updateSquiz,
      practice, setPractice,
      subpractice, setSubpractice,
      answer, setAnswer,
      mode, setMode: resetSelection(setMode),
      setFilters, removeFilter,
      detailOpen, setDetailOpen,
      breakdownOpen, setBreakdownOpen,
      guidanceOpen, setGuidanceOpen,
      nextStepsOpen, setNextStepsOpen,
      transition, setTransition,
      active: squizzes[squiz] || squizzes[squizzes.length - 1],
      latest: squizzes[squizzes.length - 1],
      resultAvailable: !!results || squiz === undefined,
      filters, lookups,
      compare, compareConsensus
    }}>
      {children}
    </context.Provider>
  )
}